// calendarUtils.js

import { startOfDay } from 'date-fns';

import {
  formatAnnualDateElapsedLabel,
  getAnnualDateOccurrencesInRange,
  getNextAnnualDateOccurrence,
  isAnnualDateFieldType,
} from 'utilities/AnnualDate';
import {
  isDatePrecision,
  normalizeDateValue,
  toDatePrecisionLocalDay,
} from 'utilities/DateTime';

const pad2 = (value) => String(value).padStart(2, '0');

/** Node types that clutter multi-type calendars (e.g. Schedule) but remain valid in their own DB views. */
export const CALENDAR_HIDDEN_NODE_TYPES = new Set(['COMMENT']);

export const normalizeCalendarNodeType = (value) => String(value || '').trim().toUpperCase();

/**
 * Whether a node type should be omitted from calendar event lists.
 * When `allowTypeKey` matches the hidden type (Comments database calendar), keep it visible.
 *
 * @param {string} nodeType
 * @param {{ allowTypeKey?: string }} [options]
 * @returns {boolean}
 */
export const isCalendarHiddenNodeType = (nodeType, { allowTypeKey } = {}) => {
  const normalized = normalizeCalendarNodeType(nodeType);
  if (!CALENDAR_HIDDEN_NODE_TYPES.has(normalized)) return false;
  return normalizeCalendarNodeType(allowTypeKey) !== normalized;
};

/**
 * @param {object} item
 * @param {{ typeKey?: string }} [options]
 * @returns {boolean}
 */
export const isCalendarEventItemVisible = (item, { typeKey } = {}) => (
  !isCalendarHiddenNodeType(item?.node?.type, { allowTypeKey: typeKey })
);

export const getCalendarTypeKey = (typeDef) => (
  typeDef?.key || typeDef?.type || ''
);

export const getCalendarDateKey = (date) => {
  const day = startOfDay(date);
  const year = day.getFullYear();
  const month = pad2(day.getMonth() + 1);
  const dayNumber = pad2(day.getDate());
  return `${year}-${month}-${dayNumber}`;
};

export const getCalendarFieldDateValue = (node, field, typeKey) => {
  if (!node || !field?.name) return null;

  const sourceKey = String(field.source || typeKey || node?.type || '').toLowerCase();
  if (!sourceKey) return null;

  const dataKey = `data_${sourceKey}`;
  const rawValue = node?.[dataKey]?.[field.name];

  if (rawValue?.startDate) {
    return normalizeDateValue(rawValue.startDate);
  }

  return normalizeDateValue(rawValue);
};

const getCalendarFieldRawValue = (node, field, typeKey) => {
  if (!node || !field?.name) return null;

  const sourceKey = String(field.source || typeKey || node?.type || '').toLowerCase();
  if (!sourceKey) return null;

  const dataKey = `data_${sourceKey}`;
  return node?.[dataKey]?.[field.name] ?? null;
};

export const getCalendarNodeTitle = (node) => {
  const title = typeof node?.title === 'string' ? node.title.trim() : '';
  return title || 'Untitled';
};

export const isTimedCalendarEvent = (item) => {
  const fieldType = String(item?.field?.type || '').toLowerCase();
  if (fieldType !== 'datetime' && fieldType !== 'datetimerange') return false;

  return item?.startPrecision === 'time' || item?.endPrecision === 'time';
};

export const getCalendarEventMetaLabel = (item) => {
  const fieldLabel = item?.field?.label || item?.field?.name || '';
  const elapsedLabel = isAnnualDateFieldType(item?.field?.type)
    ? formatAnnualDateElapsedLabel(item?.rawValue, item?.date)
    : '';

  return [fieldLabel, elapsedLabel].filter(Boolean).join(' · ');
};

export const getCalendarEventTimeLabel = (item, language = 'en') => {
  if (!isTimedCalendarEvent(item) || !item?.date) return '';

  return item.date.toLocaleTimeString(language || 'en', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const getCalendarEventTooltip = (item, language = 'en') => {
  const title = getCalendarNodeTitle(item?.node);
  const meta = getCalendarEventMetaLabel(item);
  const time = getCalendarEventTimeLabel(item, language);
  return [title, meta, time].filter(Boolean).join('\n');
};

export const compareCalendarEventItems = (a, b) => {
  const timeDiff = (a?.date?.getTime?.() || 0) - (b?.date?.getTime?.() || 0);
  if (timeDiff !== 0) return timeDiff;

  const titleA = getCalendarNodeTitle(a?.node).toLowerCase();
  const titleB = getCalendarNodeTitle(b?.node).toLowerCase();
  return titleA.localeCompare(titleB);
};

export const buildCalendarEventItems = ({ nodes, dateFields, typeKey, rangeStart = null, rangeEnd = null }) => {
  if (!Array.isArray(nodes) || nodes.length === 0 || !Array.isArray(dateFields) || dateFields.length === 0) {
    return [];
  }

  const items = [];

  nodes.forEach((node) => {
    dateFields.forEach((field) => {
      const rawValue = getCalendarFieldRawValue(node, field, typeKey);
      const fieldType = String(field?.type || '').toLowerCase();

      if (isAnnualDateFieldType(fieldType)) {
        const occurrences = rangeStart && rangeEnd
          ? getAnnualDateOccurrencesInRange(rawValue, rangeStart, rangeEnd)
          : [getNextAnnualDateOccurrence(rawValue)].filter(Boolean);

        occurrences.forEach((occurrence) => {
          items.push({
            node,
            field,
            rawValue,
            date: occurrence,
            day: startOfDay(occurrence),
            startPrecision: 'date',
            endPrecision: null,
            isRange: false,
          });
        });

        return;
      }

      let dateValue = null;
      let endDateValue = null;
      let startPrecision = null;
      let endPrecision = null;
      let isRange = false;

      if (rawValue && typeof rawValue === 'object' && rawValue.startDate) {
        dateValue = normalizeDateValue(rawValue.startDate);
        endDateValue = normalizeDateValue(rawValue.endDate);
        startPrecision = rawValue.startPrecision || null;
        endPrecision = rawValue.endPrecision || null;
        isRange = Boolean(rawValue.isRange);
      } else {
        dateValue = normalizeDateValue(rawValue);
        startPrecision = rawValue?.precision || null;
      }

      if (!dateValue) return;

      const startIsDateOnly = isDatePrecision(startPrecision);
      const endIsDateOnly = isDatePrecision(endPrecision) || (endDateValue && startIsDateOnly);
      const displayStart = startIsDateOnly
        ? (toDatePrecisionLocalDay(dateValue) || dateValue)
        : dateValue;
      const displayEnd = endDateValue
        ? (endIsDateOnly
          ? (toDatePrecisionLocalDay(endDateValue) || endDateValue)
          : endDateValue)
        : null;

      items.push({
        node,
        field,
        rawValue,
        date: displayStart,
        endDate: displayEnd,
        day: startOfDay(displayStart),
        startPrecision,
        endPrecision,
        isRange,
      });
    });
  });

  return items.sort(compareCalendarEventItems);
};

export const groupEventsByDateKey = (items, predicate = null) => {
  const map = new Map();

  items.forEach((item) => {
    if (predicate && !predicate(item)) return;

    const key = getCalendarDateKey(item.day);
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  });

  return map;
};

export const getCalendarDateHourKey = (date, hour) => (
  `${getCalendarDateKey(date)}-${pad2(hour)}`
);

export const groupAllDayEventsByDateKey = (items, predicate = null) => (
  groupEventsByDateKey(items, (item) => {
    if (isTimedCalendarEvent(item)) return false;
    if (!predicate) return true;
    return predicate(item);
  })
);

export const groupTimedEventsByDateHour = (items, predicate = null) => {
  const map = new Map();

  items.forEach((item) => {
    if (!isTimedCalendarEvent(item)) return;
    if (predicate && !predicate(item)) return;

    const hour = item?.date?.getHours?.();
    if (!Number.isFinite(hour)) return;

    const key = getCalendarDateHourKey(item.day, hour);
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  });

  map.forEach((list) => {
    list.sort(compareCalendarEventItems);
  });

  return map;
};

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_TIMED_EVENT_MINUTES = 60;
const MIN_TIMED_EVENT_MINUTES = 15;

const getMinutesFromMidnight = (date) => {
  if (!date || typeof date.getHours !== 'function') return 0;
  return (date.getHours() * 60) + date.getMinutes() + (date.getSeconds() / 60);
};

export const getTimedCalendarEventEnd = (item) => {
  const start = item?.date;
  if (!start) return null;

  const explicitEnd = item?.endDate || normalizeDateValue(item?.rawValue?.endDate);
  if (explicitEnd && explicitEnd.getTime() > start.getTime()) {
    return explicitEnd;
  }

  return new Date(start.getTime() + (DEFAULT_TIMED_EVENT_MINUTES * 60 * 1000));
};

/**
 * Lays out timed events for a single day column.
 * Returns top/height as percentages of a 24-hour column and overlap columns.
 */
export const layoutTimedCalendarEventsForDay = (items = []) => {
  const prepared = (Array.isArray(items) ? items : [])
    .filter((item) => item?.date)
    .map((item) => {
      const start = item.date;
      const end = getTimedCalendarEventEnd(item);
      const startMinutes = Math.max(0, Math.min(MINUTES_PER_DAY, getMinutesFromMidnight(start)));
      const durationMinutes = Math.max(
        MIN_TIMED_EVENT_MINUTES,
        ((end?.getTime?.() || start.getTime()) - start.getTime()) / (60 * 1000)
      );
      const endMinutes = Math.min(MINUTES_PER_DAY, startMinutes + durationMinutes);

      return {
        item,
        startMs: start.getTime(),
        endMs: end?.getTime?.() || (start.getTime() + (DEFAULT_TIMED_EVENT_MINUTES * 60 * 1000)),
        startMinutes,
        endMinutes,
      };
    })
    .sort((a, b) => {
      if (a.startMs !== b.startMs) return a.startMs - b.startMs;
      if (a.endMs !== b.endMs) return a.endMs - b.endMs;
      return compareCalendarEventItems(a.item, b.item);
    });

  if (prepared.length === 0) return [];

  const clusters = [];
  let cluster = [];
  let clusterEndMs = -Infinity;

  prepared.forEach((entry) => {
    if (cluster.length > 0 && entry.startMs >= clusterEndMs) {
      clusters.push(cluster);
      cluster = [];
      clusterEndMs = -Infinity;
    }
    cluster.push(entry);
    clusterEndMs = Math.max(clusterEndMs, entry.endMs);
  });
  if (cluster.length > 0) clusters.push(cluster);

  const layouts = [];

  clusters.forEach((clusterEntries) => {
    const columnEnds = [];
    const placed = clusterEntries.map((entry) => {
      let columnIndex = columnEnds.findIndex((endMs) => endMs <= entry.startMs);
      if (columnIndex === -1) {
        columnIndex = columnEnds.length;
        columnEnds.push(entry.endMs);
      } else {
        columnEnds[columnIndex] = entry.endMs;
      }
      return { ...entry, columnIndex };
    });

    const columnCount = Math.max(1, columnEnds.length);

    placed.forEach((entry) => {
      const durationMinutes = Math.max(MIN_TIMED_EVENT_MINUTES, entry.endMinutes - entry.startMinutes);
      layouts.push({
        item: entry.item,
        startMinutes: entry.startMinutes,
        durationMinutes,
        topPercent: (entry.startMinutes / MINUTES_PER_DAY) * 100,
        heightPercent: (durationMinutes / MINUTES_PER_DAY) * 100,
        columnIndex: entry.columnIndex,
        columnCount,
      });
    });
  });

  return layouts;
};
